import React, { useEffect, useState } from "react";
import api from "../api";
import "../Styles/SalesJournal.css";
import { useNavigate } from "react-router-dom";

import {
  Receipt,
  ArrowLeft,
  CalendarDays,
  Wallet,
  Landmark,
  ChevronRight,
  TrendingUp,
} from "lucide-react";

/* SALES ACCOUNTS */
const SALES_ACCOUNT_IDS = [22, 17, 15, 12, 10, 9, 8];

/* RECEIVING ACCOUNTS */
const RECEIVING_ACCOUNT_IDS = [14, 13, 18];

const SalesJournal = () => {

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  const now = new Date();

  const [selectedMonth, setSelectedMonth] =
    useState(now.getMonth());

  const [selectedYear, setSelectedYear] =
    useState(now.getFullYear());

  useEffect(() => {

    const loadJournal = async () => {

      try {

        setLoading(true);

        const allTxns = [];

        const accounts = await api.getAccounts();

        for (const acc of accounts) {

          const txns =
            await api.getTransactions(acc.id);

          for (const txn of txns) {

            allTxns.push({
              ...txn,
              accountId: txn.account_id,
              accountName: acc.name,
            });

          }

        }

        const grouped = {};

        for (const txn of allTxns) {

          if (!txn.reference_id) continue;

          if (!grouped[txn.reference_id]) {
            grouped[txn.reference_id] = [];
          }

          grouped[txn.reference_id].push(txn);

        }

        const journalEntries = [];

        for (const [refId, txns] of Object.entries(grouped)) {

          const creditTxn = txns.find(
            t =>
              SALES_ACCOUNT_IDS.includes(
                Number(t.accountId)
              ) &&
              parseFloat(t.credit) > 0
          );

          const debitTxn = txns.find(
            t =>
              (
                RECEIVING_ACCOUNT_IDS.includes(
                  Number(t.accountId)
                ) ||
                Number(t.accountId) === 24
              ) &&
              parseFloat(t.debit) > 0
          );

          if (creditTxn && debitTxn) {

            const txnDate =
              new Date(creditTxn.date);

            if (
              txnDate.getFullYear() === selectedYear &&
              txnDate.getMonth() === selectedMonth
            ) {

              journalEntries.push({

                date: creditTxn.date,

                particulars:
                  creditTxn.details,

                amount: parseFloat(
                  creditTxn.credit
                ),

                receivedVia:
                  Number(debitTxn.accountId) === 24
                    ? "Opening Adjustment"
                    : debitTxn.accountName,

              });

            }

          }

          /* FOREX SALES */

          const forexCreditTxn = txns.find(
            t =>
              [26, 27].includes(
                Number(t.accountId)
              ) &&
              parseFloat(t.credit) > 0
          );

          const forexDebitTxn = txns.find(
            t =>
              (
                RECEIVING_ACCOUNT_IDS.includes(
                  Number(t.accountId)
                ) ||
                Number(t.accountId) === 24
              ) &&
              parseFloat(t.debit) > 0
          );

          if (
            forexCreditTxn &&
            forexDebitTxn
          ) {

            const txnDate =
              new Date(forexCreditTxn.date);

            if (
              txnDate.getFullYear() === selectedYear &&
              txnDate.getMonth() === selectedMonth
            ) {

              journalEntries.push({

                date:
                  forexCreditTxn.date,

                particulars:
                  `Sale of ${
                    forexCreditTxn.accountId === 26
                      ? "USD"
                      : "CAN"
                  } - ${
                    forexCreditTxn.details
                  }`,

                amount:
                  parseFloat(
                    forexDebitTxn.debit
                  ),

                receivedVia:
                  Number(
                    forexDebitTxn.accountId
                  ) === 24
                    ? "Opening Adjustment"
                    : forexDebitTxn.accountName,

              });

            }

          }

        }

        journalEntries.sort(
          (a, b) =>
            new Date(a.date) -
            new Date(b.date)
        );

        setEntries(journalEntries);

      } catch (err) {

        console.error(
          "❌ Failed to load sales journal:",
          err
        );

      } finally {

        setLoading(false);

      }

    };

    loadJournal();

  }, [selectedMonth, selectedYear]);

  const total = entries.reduce(
    (sum, e) => sum + e.amount,
    0
  );

  if (loading) {

    return (

      <div className="sales-page">

        <div className="sales-loading-card">

          <div className="sales-spinner"></div>

          <p>
            Loading Sales Journal...
          </p>

        </div>

      </div>

    );

  }

  return (

    <div className="sales-page">

      {/* HERO */}

      <div className="sales-hero">

        <div className="sales-hero-left">

          <div className="sales-pill">
            <Receipt size={15} />
            <span>Sales Journal</span>
          </div>

          <h1>
            Sales
            <br />
            Management
          </h1>

          <p>
            Monitor sales activity,
            incoming revenue,
            forex sales and
            customer payment flows
            across your accounting system.
          </p>

          <div className="sales-actions">

            <button
              className="sales-btn primary"
              onClick={() =>
                navigate("/journals")
              }
            >
              Open Journals
              <ChevronRight size={17} />
            </button>

            <button
              className="sales-btn secondary"
              onClick={() =>
                navigate("/accounts")
              }
            >
              <ArrowLeft size={16} />
              Accounts
            </button>

          </div>

        </div>

        {/* RIGHT */}

        <div className="sales-summary-grid">

          <div className="summary-card green">

            <div className="summary-icon">
              <TrendingUp size={28} />
            </div>

            <div>
              <span>Total Sales</span>

              <h2>
                $
                {total.toLocaleString()}
              </h2>
            </div>

          </div>

          <div className="summary-card blue">

            <div className="summary-icon">
              <CalendarDays size={28} />
            </div>

            <div>
              <span>Transactions</span>

              <h2>
                {entries.length}
              </h2>
            </div>

          </div>

          <div className="summary-card teal">

            <div className="summary-icon">
              <Landmark size={28} />
            </div>

            <div>
              <span>Selected Period</span>

              <h2>
                {new Date(
                  selectedYear,
                  selectedMonth
                ).toLocaleString(
                  "en-US",
                  {
                    month: "long",
                    year: "numeric",
                  }
                )}
              </h2>

            </div>

          </div>

        </div>

      </div>

      {/* FILTERS */}

      <div className="sales-filter-bar">

        <div className="filter-group">

          <label>Month</label>

          <select
            value={selectedMonth}
            onChange={(e) =>
              setSelectedMonth(
                parseInt(e.target.value)
              )
            }
          >

            {Array.from(
              { length: 12 },
              (_, i) => (

                <option
                  key={i}
                  value={i}
                >
                  {new Date(
                    0,
                    i
                  ).toLocaleString(
                    "en-US",
                    {
                      month: "long",
                    }
                  )}
                </option>

              )
            )}

          </select>

        </div>

        <div className="filter-group">

          <label>Year</label>

          <select
            value={selectedYear}
            onChange={(e) =>
              setSelectedYear(
                parseInt(e.target.value)
              )
            }
          >

            {Array.from(
              { length: 5 },
              (_, i) => {

                const year =
                  now.getFullYear() - 2 + i;

                return (

                  <option
                    key={year}
                    value={year}
                  >
                    {year}
                  </option>

                );

              }
            )}

          </select>

        </div>

      </div>

      {/* TABLE */}

      <div className="sales-table-wrapper">

        <table className="sales-table">

          <thead>

            <tr>
              <th>Date</th>
              <th>Particulars</th>
              <th>Received Via</th>
              <th>Amount</th>
            </tr>

          </thead>

          <tbody>

            {entries.map((e, i) => (

              <tr key={i}>

                <td>
                  {e.date.split("T")[0]}
                </td>

                <td>
                  {e.particulars}
                </td>

                <td>
                  {e.receivedVia}
                </td>

                <td className="sales-amount">
                  $
                  {e.amount.toLocaleString()}
                </td>

              </tr>

            ))}

            <tr className="total-row">

              <td colSpan="3">
                Total Sales
              </td>

              <td>
                $
                {total.toLocaleString()}
              </td>

            </tr>

          </tbody>

        </table>

      </div>

    </div>

  );

};

export default SalesJournal;